import React, { useState, useEffect } from 'react';
import { X, MessageCircle, CheckCircle, AlertCircle, Loader, User, Clock, Tag } from 'lucide-react';
import { Task, AIAnalysis, ConnectionRequest } from '../types';
import AIAnalysisDisplay from './AIAnalysisDisplay';
import { useAuth } from '../contexts/AuthContext';
import { supabase, TABLES } from '../api/db';
import { sendConnectionRequest, checkConnectionRequest } from '../api/connections';
import { notifyConnectionRequest } from '../api/notifications';
import { getTaskerProfileByUserId } from '../api/taskerProfiles';
import { normalizeAIAnalysisRow, normalizeTaskRow } from '../utils/taskRecords';

interface TaskDetailModalProps {
  taskId: string;
  isOpen: boolean;
  onClose: () => void;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ taskId, isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const [task, setTask] = useState<Task | null>(null);
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [matchScore, setMatchScore] = useState<number | null>(null);
  const [connectionRequest, setConnectionRequest] = useState<ConnectionRequest | null>(null);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState('');

  useEffect(() => {
    if (isOpen && taskId) {
      loadTaskData();
    } else {
      // Reset state when modal closes
      setTask(null);
      setAnalysis(null);
      setConnectionRequest(null);
      setShowConnectionForm(false);
      setConnectionMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, taskId, currentUser]);

  useEffect(() => {
    // Close on ESC key
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const loadTaskData = async () => {
    if (!taskId) return;

    setLoading(true);
    try {
      const { data: taskRow } = await supabase.from(TABLES.TASKS).select('*').eq('id', taskId).maybeSingle();
      if (taskRow) {
        const taskData = normalizeTaskRow(taskRow as Record<string, unknown>);
        setTask(taskData);

        const { data: analysisRows } = await supabase.from(TABLES.AI_ANALYSES).select('*').eq('task_id', taskId).limit(1);
        if (analysisRows && analysisRows.length > 0) {
          setAnalysis(normalizeAIAnalysisRow(analysisRows[0] as Record<string, unknown>));
        }

        if (currentUser) {
          await calculateMatchScore(taskData);
          await checkExistingConnection(taskId);
        }
      }
    } catch (error) {
      console.error('Error loading task data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMatchScore = async (taskData: Task) => {
    if (!currentUser) return;

    try {
      const profile = await getTaskerProfileByUserId(currentUser.uid);
      if (!profile) {
        setMatchScore(null);
        return;
      }

      const userSkills = [
        ...(profile.skills?.primary || []),
        ...(profile.skills?.secondary || [])
      ];

      // Simple matching: count matching skills/tags
      const taskTags = taskData.tags || [];
      const matchingSkills = userSkills.filter(skill =>
        taskTags.some(tag => tag.toLowerCase().includes(skill.toLowerCase()))
      );

      // Calculate score (0-100)
      const score = taskTags.length > 0
        ? Math.round((matchingSkills.length / Math.max(taskTags.length, userSkills.length)) * 100)
        : 50; // Default score if no tags

      setMatchScore(score);
    } catch (error) {
      console.error('Error calculating match score:', error);
      setMatchScore(null);
    }
  };

  const checkExistingConnection = async (taskId: string) => {
    if (!currentUser) return;

    try {
      const request = await checkConnectionRequest(taskId, currentUser.uid);
      if (request) {
        setConnectionRequest(request);
      }
    } catch (error) {
      console.error('Error checking connection:', error);
    }
  };

  const handleSendConnectionRequest = async () => {
    if (!currentUser || !task || sendingRequest) return;

    setSendingRequest(true);
    try {
      let taskerName = currentUser.displayName || currentUser.email || 'A Tasker';
      try {
        const profile = await getTaskerProfileByUserId(currentUser.uid);
        if (profile?.personalInfo?.displayName) {
          taskerName = profile.personalInfo.displayName;
        }
      } catch (err) {
        // Use default name
      }

      const taskOwnerId = task.userId;
      const requestId = await sendConnectionRequest(
        task.id,
        currentUser.uid,
        taskOwnerId,
        connectionMessage || undefined
      );

      // Send notification to task owner
      try {
        await notifyConnectionRequest(
          taskOwnerId,
          currentUser.uid,
          task.id,
          task.title,
          taskerName,
          requestId
        );
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
        // Don't fail the request if notification fails
      }

      // Reload connection request to get full data
      const newRequest = await checkConnectionRequest(task.id, currentUser.uid);
      if (newRequest) {
        setConnectionRequest(newRequest);
      }

      setShowConnectionForm(false);
      setConnectionMessage('');
    } catch (error: any) {
      console.error('Error sending connection request:', error);
      alert('Failed to send connection request: ' + (error.message || 'Unknown error'));
    } finally {
      setSendingRequest(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 rounded-2xl border border-purple-300/30 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-purple-300/20 bg-black/20">
            <h2 className="text-2xl font-bold text-purple-100">
              {task?.title || 'Task Details'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition text-purple-200 hover:text-white"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            {loading ? (
              <div className="text-center py-12">
                <Loader className="w-12 h-12 text-purple-300 mx-auto animate-spin" />
                <p className="text-purple-200 mt-4">Loading task details...</p>
              </div>
            ) : task ? (
              <div className="space-y-6">
                {/* Task Info */}
                <div>
                  <div className="flex items-center gap-4 text-sm text-purple-300/70 mb-4">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{task.userName || 'Anonymous'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{task.createdAt ? new Date(task.createdAt).toLocaleDateString() : ''}</span>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-purple-100 mb-2">Description</h3>
                  <p className="text-purple-200 mb-4">{task.summary}</p>

                  {task.tags && task.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {task.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-purple-500/20 border border-purple-400/50 rounded-full text-sm text-purple-200"
                        >
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Match Score */}
                {currentUser && matchScore !== null && (
                  <div className="bg-purple-500/10 border border-purple-400/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-purple-200 font-medium">Your Match Score</span>
                      <span className="text-2xl font-bold text-purple-300">{matchScore}%</span>
                    </div>
                    <div className="w-full bg-purple-900/30 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                        style={{ width: `${matchScore}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* AI Analysis */}
                {analysis && (
                  <div>
                    <h3 className="text-xl font-semibold text-purple-100 mb-4">AI Analysis</h3>
                    <AIAnalysisDisplay analysis={analysis} />
                  </div>
                )}

                {/* Connection Request Form */}
                {currentUser && task.userId !== currentUser.uid && (
                  <div className="border-t border-purple-300/20 pt-6">
                    {connectionRequest ? (
                      <div className="bg-white/5 border border-purple-300/30 rounded-xl p-4">
                        {connectionRequest.status === 'pending' && (
                          <div className="flex items-center gap-2 text-yellow-200">
                            <AlertCircle className="w-5 h-5" />
                            <span>Connection request pending</span>
                          </div>
                        )}
                        {connectionRequest.status === 'accepted' && (
                          <div className="flex items-center gap-2 text-green-200">
                            <CheckCircle className="w-5 h-5" />
                            <span>Connection request accepted!</span>
                          </div>
                        )}
                        {connectionRequest.status === 'rejected' && (
                          <div className="flex items-center gap-2 text-red-200">
                            <X className="w-5 h-5" />
                            <span>Connection request was rejected</span>
                          </div>
                        )}
                      </div>
                    ) : showConnectionForm ? (
                      <div className="bg-white/5 border border-purple-300/30 rounded-xl p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-purple-100">Send Connection Request</h4>
                        <textarea
                          value={connectionMessage}
                          onChange={(e) => setConnectionMessage(e.target.value)}
                          placeholder="Optional: Add a message to introduce yourself..."
                          rows={4}
                          className="w-full px-4 py-3 bg-white/5 border border-purple-300/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 resize-none"
                        />
                        <div className="flex gap-3">
                          <button
                            onClick={handleSendConnectionRequest}
                            disabled={sendingRequest}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {sendingRequest ? (
                              <>
                                <Loader className="w-4 h-4 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <MessageCircle className="w-4 h-4" />
                                Send Request
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setShowConnectionForm(false);
                              setConnectionMessage('');
                            }}
                            className="px-4 py-2 bg-white/5 border border-purple-300/30 rounded-lg font-medium hover:bg-white/10 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowConnectionForm(true)}
                        className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Send Connection Request
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-purple-300/50 mx-auto mb-4" />
                <p className="text-purple-200">Task not found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TaskDetailModal;
